// Third Party
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import debounce from 'lodash/debounce';

// Components
import { OverlappingBackground, Header } from '../../components/layout';
import { ErrorBoundary } from '../../components/errors';
import MostSearchedFunds from './MostSearchedFunds';
import ActivityLogs from './ActivityLogs';
import Advisors from './Advisors';
import { Divider } from '../../components/misc';
import { LoadingSpinner } from '../../components/common';

// Actions
import { ReduxState } from '../../../redux/modules/types';
import { getMostSearchedFunds } from '../../../redux/modules/analytics';
import { getAllAdvisorsActivity, getAdvisorActivity, resetAdvisorActivity } from '../../../redux/modules/activity-log';
import { getMyAdvisors } from '../../../redux/modules/advisors';

// Utils
import { FormattedMessage, formatMessage } from '../../../utils/i18n';
import { ACTIVITY_FORMAT, getNameFromActivities } from '../../../utils/activity-log';
import AdvisorInfos from '@/types/advisor-infos';
import Activity from '@/types/activity';
import MostSearchedFund from '@/types/most-searched-fund';

type StateProps = {
  advisors: AdvisorInfos[];
  activityLogs: Activity[];
  focusedActivityLogs: Activity[];
  mostSearchedFunds: MostSearchedFund[];
  loading: {
    mostSearchedFunds: boolean;
    advisors: boolean;
    activityLogs: boolean;
  };
  nextActivityLogToken?: string;
  nextActivityLogTokenFocused?: string;
};

type DispatchProps = {
  getMostSearchedFunds: () => Promise<MostSearchedFund[]>;
  getMyAdvisors: () => Promise<AdvisorInfos[]>;
  getAllAdvisorsActivity: (nextToken?: string) => void;
  getAdvisorActivity: (id: string, nextToken?: string) => void;
  resetAdvisorActivity: () => void;
};

interface State {
  maxFunds: number;
  focusedUserId?: string;
}

type Props = StateProps & DispatchProps & RouteComponentProps;

class Insights extends Component<Props, State> {
  readonly state: State = {
    maxFunds: 5,
  };

  handleResize = debounce(() => {
    this.adjustMaxFunds();
  }, 50);

  componentDidMount() {
    window.addEventListener('resize', this.handleResize);

    this.props.getMostSearchedFunds();
    this.props.getMyAdvisors();
    this.props.getAllAdvisorsActivity();
    this.adjustMaxFunds();
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }

  adjustMaxFunds = () => {
    const { maxFunds } = this.state;

    const ADJUST_AT_HEIGHT = 920;
    const newMaxFunds = window.innerHeight < ADJUST_AT_HEIGHT ? 5 : 10;

    if (newMaxFunds !== maxFunds) {
      this.setState({ maxFunds: newMaxFunds });
    }
  };

  renderMostSearchedFunds = () => {
    const { mostSearchedFunds, loading } = this.props;
    const { maxFunds } = this.state;

    const items = mostSearchedFunds.slice(0, maxFunds);

    return (
      <>
        <div className="section-header">
          <h3 className="section-title">
            <FormattedMessage id="Insights.MostSearchedFunds.Title" />
          </h3>
        </div>

        {loading.mostSearchedFunds && <LoadingSpinner className="global-loading-spinner" />}

        {!loading.mostSearchedFunds && <MostSearchedFunds items={items} />}
      </>
    );
  };

  renderAdvisors = () => {
    const { advisors, loading } = this.props;

    return (
      <>
        <div className="section-header">
          <h3 className="section-title">
            <FormattedMessage id="Insights.Advisors.Title" />
          </h3>
        </div>

        {loading.advisors && <LoadingSpinner className="global-loading-spinner" />}

        {!loading.advisors && <Advisors items={advisors} />}
      </>
    );
  };

  onLoadMoreActivity = () => {
    const { nextActivityLogToken, nextActivityLogTokenFocused } = this.props;
    const { focusedUserId } = this.state;

    if (focusedUserId) {
      this.props.getAdvisorActivity(focusedUserId, nextActivityLogTokenFocused);
    } else {
      this.props.getAllAdvisorsActivity(nextActivityLogToken);
    }
  };

  viewContactActivity = (id: string) => {
    const { nextActivityLogTokenFocused } = this.props;

    this.props.resetAdvisorActivity();
    this.props.getAdvisorActivity(id, nextActivityLogTokenFocused);
    this.setState({ focusedUserId: id });
  };

  goBack = () => {
    this.setState({ focusedUserId: undefined });
  };

  renderActivityLog = () => {
    const { activityLogs, loading, nextActivityLogToken, nextActivityLogTokenFocused, focusedActivityLogs } = this.props;
    const { focusedUserId } = this.state;

    if (focusedUserId) {
      const name = getNameFromActivities(focusedActivityLogs);

      return (
        <>
          <div className="insights-content contact-header">
            <div onClick={this.goBack} className="back-section d-flex align-items-center">
              <i className="icon-cheveron-left" />
              <FormattedMessage id="ActivityLog.Back" />
            </div>
            {name && (
              <h3 data-private data-dd-privacy="mask">
                {formatMessage('ActivityLog.ContactNameTitle', { name })}
              </h3>
            )}
          </div>

          <div className="insights-content focused-activity-section">
            <ActivityLogs
              className="focused"
              activityLogs={focusedActivityLogs}
              loading={loading.activityLogs}
              hasMore={!!nextActivityLogTokenFocused}
              onLoadMoreActivity={this.onLoadMoreActivity}
              viewContactActivity={this.viewContactActivity}
              format={ACTIVITY_FORMAT.CONTACT}
            />
          </div>
        </>
      );
    }

    return (
      <ActivityLogs
        activityLogs={activityLogs}
        loading={loading.activityLogs}
        hasMore={!!nextActivityLogToken}
        onLoadMoreActivity={this.onLoadMoreActivity}
        viewContactActivity={this.viewContactActivity}
        displayAsCards
        format={ACTIVITY_FORMAT.INSIGHTS}
      />
    );
  };

  render() {
    return (
      <>
        <Header />
        <OverlappingBackground />

        <main id="content" className="insights d-flex flex-fill">
          <ErrorBoundary>
            <div className="left-container d-flex flex-column">
              <h3>
                <FormattedMessage id="Insights.Title" />
              </h3>

              <div className="insights-content funds-table">{this.renderMostSearchedFunds()}</div>

              <div className="insights-content advisors-table">{this.renderAdvisors()}</div>
            </div>

            <Divider />

            <div className="right-container">
              <h3>
                <FormattedMessage id="Insights.ActivityLog" />
              </h3>

              {this.renderActivityLog()}
            </div>
          </ErrorBoundary>
        </main>
      </>
    );
  }
}

const mapStateToProps = (state: ReduxState): StateProps => {
  const { analytics, advisors, activityLogs } = state;

  return {
    advisors: advisors.advisors,
    mostSearchedFunds: analytics.mostSearchedFunds,
    activityLogs: activityLogs.activityLogs,
    focusedActivityLogs: activityLogs.focusedActivityLogs,
    loading: {
      mostSearchedFunds: analytics.loading.mostSearchedFunds,
      advisors: advisors.loading,
      activityLogs: activityLogs.loading,
    },
    nextActivityLogToken: activityLogs.nextToken,
    nextActivityLogTokenFocused: activityLogs.nextTokenFocused,
  };
};

export default withRouter(
  connect(mapStateToProps, {
    getMostSearchedFunds,
    getMyAdvisors,
    getAllAdvisorsActivity,
    getAdvisorActivity,
    resetAdvisorActivity,
  })(Insights),
);
